import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { AddShoppingCart, FavoriteBorder, LocationOn } from '@mui/icons-material';
import {
  Badge,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CallIcon from '@mui/icons-material/Call';

import { useShop } from '../../context/ShopContext'; 
// --- AUTH IMPORTS ---
import { useAuth } from '../../context/AuthContext'; 
import UserProfileDropdown from './UserProfileDropdown'; 
import Login from './Login'; 
// --- END AUTH IMPORTS ---

import Slogo from '../../assests/Slogo.png'; 

// Define a type for categories
interface Category {
  title: string;
  href: string; 
}

const categories: Category[] = [
  { title: "EARRINGS", href: "/earrings" },
  { title: "NECKLACES", href: "/necklaces" },
  { title: "BANGLES", href: "/bangles" },
  { title: "CHAINS", href: "/chains" },
  { title: "RINGS", href: "/rings" },
  { title: "PENDANTS", href: "/pendants" },
  { title: "BRACELETS", href: "/bracelets" },
  { title: "COINS & BARS", href: "/coins-bars" },
  { title: "ALL JEWELLERY", href: "/all-jewellery" },
  { title: "COLLECTIONS", href: "/collections" },
];

// Helper Component for the marquee text (Vertical Ticker)
const VerticalTicker: React.FC = () => {
    const messages = [
        "✨ FREE shipping on orders of 6 items or more ! ✨",
        "🎁 Get 10% OFF on your first purchase !",
        "🔥 Exclusive Diwali Collections are LIVE now!",
    ];

    const displayMessages = [...messages, messages[0]]; 
    const [activeIndex, setActiveIndex] = useState(0);
    const [transitionDuration, setTransitionDuration] = useState('duration-700'); 
    const intervalTime = 3000; 
    const slideDuration = 800; 
    
    // Increased height to 32px to give text enough breathing room on mobile
    const itemHeight = 32; 
    const translateY = `-${activeIndex * itemHeight}px`;

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveIndex((current) => {
                const nextIndex = current + 1;
                if (nextIndex === displayMessages.length - 1) {
                    setTimeout(() => {
                        setTransitionDuration('duration-0');
                        setActiveIndex(0);
                        setTimeout(() => {
                            setTransitionDuration('duration-700');
                        }, 50); 
                    }, slideDuration); 
                    return nextIndex; 
                }
                return nextIndex % displayMessages.length;
            });
        }, intervalTime); 
        return () => clearInterval(interval);
    }, [displayMessages.length]); 

    return (
        // Changed to h-8 (32px) and relative positioning
        <div className="text-center font-semibold text-yellow-300 overflow-hidden h-8 w-full relative">
            <div 
                className={`absolute top-0 left-0 w-full transition-transform ${transitionDuration} ease-in-out`} 
                style={{ transform: `translateY(${translateY})` }} 
            >
               {displayMessages.map((msg, index) => (
                   // Wrap each message in a flex container that perfectly matches the itemHeight
                   <div 
                        key={index} 
                        className="h-8 flex items-center justify-center w-full px-2"
                   >
                       <span className="truncate text-xs sm:text-sm">
                            {msg}
                        </span>
                   </div>
               ))}
            </div>
        </div>
    );
};

const Navbar: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  
  const { cartItems = [], wishlistItems = [] } = useShop() || {}; 
  const { user } = useAuth() || {}; 
  const isLoggedIn = !!user; 

  const googleMapsLink = "https://maps.google.com/?q=" + encodeURIComponent("Sardar Jewellers, Kalyanpur, Kanpur, UP");

  return (
    <header className='sticky top-0 z-50 w-full bg-transparent'>
      
      {/* Top Bar */}
      <div className="bg-amber-950 text-white p-2 px-4 text-xs md:text-sm flex justify-between items-center relative z-10 border-b border-amber-800">

        {/* Hidden on small screens to prioritize the ticker */}
        <div className="hidden md:flex items-center gap-2 justify-start w-1/3">
          <LocationOn sx={{ fontSize: 18, color: "gold" }} />
          <a
            href={googleMapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline cursor-pointer flex items-center gap-1"
            aria-label="View address on Google Maps"
          >
            Address: <span className='whitespace-nowrap'>Kalyanpur, Kanpur, UP</span>
          </a>
        </div>
        
        <div className='flex justify-center w-full md:w-1/3'>
            <VerticalTicker />
        </div>

        {/* Hidden on extra small screens, formatted as a clickable tel link for SEO/Mobile */}
        <div className="hidden sm:flex items-center gap-2 justify-end whitespace-nowrap w-1/3">
          <CallIcon sx={{ fontSize: 18, color: "gold" }} />
          <a href="tel:+918441856072" className="hover:underline font-medium" aria-label="Call Contact Support">
            Contact Us: +91 8441856072
          </a>
        </div>

      </div>

      {/* Middle Bar - Logo, Search, and Action Icons */}
      <Box className='bg-white shadow-sm relative z-10'>
        <div className='flex justify-between items-center px-3 lg:px-8 h-[75px] border-b'>
          
          <div className='flex items-center gap-2'>
            {!isLarge && (
              <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open mobile menu">
                <MenuIcon />
              </IconButton>
            )}
            <Link to="/" className='flex items-center gap-2 sm:gap-3 cursor-pointer' aria-label="Go to homepage"> 
              <img src={Slogo} alt='Sardar Jewellers Logo' className='w-12 h-12 sm:w-16 sm:h-16 object-contain' />
              {/* Changed h1 to span for better SEO structure across multiple pages */}
              <span className='logo text-lg sm:text-xl md:text-2xl font-bold text-primary-color'>
                Sardar Jewellers
              </span>
            </Link> 
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            
            {/* Wishlist Icon (Commented out but prepared for responsiveness) */}
            {/* <IconButton aria-label="Go to wishlist">
                <Badge badgeContent={wishlistItems.length} color="primary">
                  <FavoriteBorder />
                </Badge>
            </IconButton> */}

             <Link to="/cart" aria-label="View shopping cart"> 
                <IconButton aria-label="shopping cart">
                    <Badge badgeContent={cartItems.length} color="primary">
                      <AddShoppingCart />
                    </Badge>
                </IconButton>
              </Link>

            {/* Login/User Section */}
            <div className="flex items-center">
                {isLoggedIn ? (
                    <UserProfileDropdown /> 
                ) : (
                   <button
                    onClick={() => setShowLogin(true)}
                    aria-label="Login to your account"
                    className="flex items-center space-x-1 
                      text-white bg-[#00927c] 
                      transition-all duration-300 
                      px-3 py-2 rounded-lg 
                      hover:bg-transparent 
                      hover:border hover:border-[#00927c] 
                      hover:text-[#00927c]"
                    >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path></svg>
                        <span className="hidden sm:inline font-medium">Login</span>
                    </button>
                )}
            </div>
          </div>
        </div>
      </Box>

      {/* Navigation Links Bar (Desktop Only) */}
      {isLarge && (
        <nav aria-label="Main Navigation" className="flex flex-wrap justify-center gap-6 xl:gap-8 p-3 text-sm font-medium bg-white shadow-md border-b">
          {categories.map((cat, idx) => (
            <Link
              key={idx}
              to={cat.href}
              className="px-2 py-1 cursor-pointer
                         hover:text-[#8B5E3C]
                         border-b-2 border-transparent hover:border-[#8B5E3C]
                         transition-colors duration-200 ease-in-out whitespace-nowrap"
            >
              {cat.title}
            </Link>
          ))}
        </nav>
      )}

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280 }} role="presentation" onClick={() => setDrawerOpen(false)}>
          <List>
            <div className="p-4 flex items-center border-b mb-2 bg-gray-50">
              <img src={Slogo} alt='Sardar Jewellers Logo' className='w-12 h-12 mr-3 object-contain' />
              <span className='text-lg font-bold text-primary-color'>Sardar Jewellers</span>
            </div>
            
            {/* Replaced <a> with <Link> for React Router efficiency */}
            {categories.map((cat, idx) => (
              <ListItem key={idx} disablePadding>
                <Link to={cat.href} className="w-full px-4 py-3 font-medium text-gray-800 hover:bg-gray-100 transition-colors">
                  {cat.title}
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Login Modal */}
      {showLogin && <Login open={true} onClose={() => setShowLogin(false)} />}
    </header> 
  );
};

export default Navbar;